import { NotificationService } from "../../api/notification/notification.service";
import {Component, ElementRef, Inject, ViewChild} from "@angular/core";
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { Education, Talent, TrackRecord } from "../../api/sign-up/signup.types";
import {Store} from "@ngrx/store";
import {talentProfileFeatureName, TalentProfileState} from "../../api/profile/store/talent-profile.reducer";
import * as TalentProfileActions from "../../api/profile/store/talent-profile.action";
import {filter, forkJoin, map, take} from "rxjs";
import {EducationsInputComponent} from "../../common-componnets/educations-input/educations-input.component";
import {TrackRecordsInputComponent} from "../../common-componnets/track-record-input/track-records-input.component";
import { TranslateService } from "@ngx-translate/core";
import { CmsCachedService } from "../../api/cms/cms-cached.service";
import { CmsNameValue } from "../../api/cms/cms.types";

@Component({
  selector: "mh-review-parsed-cv-dialog",
  templateUrl: "./review-parsed-cv-dialog.component.html",
  styleUrls: ["./review-parsed-cv-dialog.component.less"],
})
export class ReviewParsedCvDialogComponent {
  firstName: string = '';
  lastName: string = '';
  email: string = '';
  phoneNumber: string = '';
  city: string = '';
  postalCode: string = '';
  country: string = '';
  
  jobTitle: string = '';
  yearsOfExperience: number = 0;
  competences: string[] = [];
  nonTechnicalCompetences: string[] = [];
  certifications: string[] = [];
  languages: string[] = [];

  mappedEducations: Education[] = [];
  mappedTrackRecords: TrackRecord[] = [];

  parsedCV: any;
  talent: Talent;

  loading = false;
  parsedCvReady = false;
  educationValidationError = "";
  trackRecordValidationError = "";

  get progressPercentage(): number {
    const educations = this.educationsInputComponent?.value || this.mappedEducations;
    const trackRecords = this.trackRecordsInputComponent?.value || this.mappedTrackRecords;
    let total = 1;
    let completed = this.yearsOfExperience > 0 ? 1 : 0;

    trackRecords.forEach((record: TrackRecord) => {
      const startDate = record.workingUntilNow ? record.employmentDurationFromToNow : record.employmentDurationRange?.[0];
      const endDate = record.workingUntilNow ? true : record.employmentDurationRange?.[1];
      const fields = [
        record.companyName,
        record.companyType?.name,
        record.sector?.name,
        record.industry?.name,
        record.department?.name,
        record.jobTitle,
        record.roleDetails,
        startDate,
        endDate,
      ];
      total += fields.length;
      completed += fields.filter((field) => !!field && (typeof field !== "string" || !!field.trim())).length;
    });

    educations.forEach((education: Education) => {
      const fields = [education.universityName, education.courseOfStudy, education.graduationYear, education.degree?.name];
      total += fields.length;
      completed += fields.filter((field) => !!field && (typeof field !== "string" || !!field.trim())).length;
    });

    total += 1;
    completed += this.certifications.length > 0 ? 1 : 0;

    return total ? Math.round((completed / total) * 100) : 0;
  }

  @ViewChild(EducationsInputComponent) educationsInputComponent!: EducationsInputComponent;
  @ViewChild(TrackRecordsInputComponent) trackRecordsInputComponent!: TrackRecordsInputComponent;
  @ViewChild("trackRecordSection") trackRecordSection!: ElementRef<HTMLElement>;
  @ViewChild("educationSection") educationSection!: ElementRef<HTMLElement>;

  constructor(
    @Inject(NZ_MODAL_DATA) public modalData: any,
    private modal: NzModalRef,
    private readonly store: Store<{ [talentProfileFeatureName]: TalentProfileState }>,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private cmsCachedService: CmsCachedService,
  ) {
    this.parsedCV = this.modalData["parsedCV"];
    this.talent = this.modalData["talent"];
    this.loadParsedCv(this.parsedCV);
  }

  private loadParsedCv(parsedCV: any): void {
    if (!parsedCV) return;

    forkJoin({
      degree: this.cmsCachedService.getUserDataByName("degree"),
      sector: this.cmsCachedService.getUserDataByName("sector"),
      industry: this.cmsCachedService.getUserDataByName("industry"),
      companyType: this.cmsCachedService.getUserDataByName("company-type"),
      department: this.cmsCachedService.getUserDataByName("expert-field"),
    }).pipe(take(1)).subscribe((options) => this.mapParsedCv(parsedCV, options));
  }

  private mapParsedCv(parsedCV: any, options: {
    degree: CmsNameValue[];
    sector: CmsNameValue[];
    industry: CmsNameValue[];
    companyType: CmsNameValue[];
    department: CmsNameValue[];
  }): void {
    // The parser currently returns the extracted fields directly in `results`.
    // Keep these values local to the review form; nothing is persisted until
    // the user explicitly clicks "Apply approved changes".
    const payload: any = parsedCV.results || parsedCV;
    const parsedProfile = payload.parsedProfile || payload;

    this.firstName = parsedProfile.name || payload.firstName || "";
    this.lastName = parsedProfile.surname || payload.lastName || "";
    this.email = parsedProfile.email || payload.email || "";
    this.phoneNumber = parsedProfile.phoneNumber || payload.phoneNumber || "";
    this.city = parsedProfile.location || payload.contactData?.city || "";

    this.jobTitle = parsedProfile.jobTitle || "";
    this.yearsOfExperience = parsedProfile.yearsOfExperience || 0;
    this.competences = [];
    this.nonTechnicalCompetences = parsedProfile.nonTechnicalCompetences || [];
    this.certifications = (parsedProfile.qualifications || parsedProfile.certifications || []).map((c: any) =>
      typeof c === "string" ? c : `${c.name || ""} (${c.school || ""}${c.year ? `, ${c.year}` : ""})`,
    );
    this.languages = (parsedProfile.languages || []).map((l: any) =>
      typeof l === "string" ? l : `${l.language || l.name || ""} - ${l.level || ""}`,
    );

    this.mappedEducations = (parsedProfile.educations || parsedProfile.education || []).map((edu: any) => ({
      universityName: edu.universityName,
      courseOfStudy: edu.profession || edu.courseOfStudy,
      graduationYear: edu.year ? new Date(edu.year.toString()) : edu.graduationYear,
      degree: this.findCmsValue(edu.degree, options.degree, edu.levelOfDegree),
    }));

    this.mappedTrackRecords = (parsedProfile.experiences || parsedProfile.trackRecord || []).map((exp: any) => {
      const dateTo = typeof exp.dateTo === "string" ? exp.dateTo.toLowerCase() : exp.dateTo;
      const isPresent = !dateTo || (typeof dateTo === "string" && dateTo.includes("present"));
      return {
        jobTitle: exp.jobTitle,
        companyName: exp.company || exp.companyName,
        companyType: this.findCmsValue(exp.companyType, options.companyType),
        sector: this.findCmsValue(exp.sector, options.sector),
        industry: this.findCmsValue(exp.industry, options.industry),
        // TrackRecord supports one department. The parser sends expertFields,
        // whose values come from the same CMS list used by the department field.
        department: this.findCmsValue(exp.department || exp.expertFields?.[0], options.department),
        roleDetails:
          (exp.details || exp.roleDetails || "") +
          (exp.achievements && exp.achievements.length > 0
            ? `\n${this.translateService.instant("cv-review.data.achievements")}\n- ${exp.achievements.join("\n- ")}`
            : ""),
        current: exp.current !== undefined ? exp.current : isPresent,
        workingUntilNow: exp.workingUntilNow !== undefined ? exp.workingUntilNow : isPresent,
        employmentDurationRange: exp.employmentDurationRange || [
          exp.dateFrom ? new Date(exp.dateFrom) : undefined,
          !isPresent && exp.dateTo ? new Date(exp.dateTo) : undefined,
        ],
        employmentDurationFromToNow: exp.dateFrom ? new Date(exp.dateFrom) : undefined,
      };
    });
    this.parsedCvReady = true;
  }

  private findCmsValue(value: any, options: CmsNameValue[], fallbackName?: string): CmsNameValue | undefined {
    const valueId = typeof value === "object" ? value?.value : value;
    if (valueId !== undefined && valueId !== null) {
      const matchingOption = options.find((option) => option.value === Number(valueId));
      if (matchingOption) return matchingOption;
    }

    if (!fallbackName) return undefined;
    const normalizedName = fallbackName.trim().toLowerCase();
    return options.find((option) => option.name.trim().toLowerCase() === normalizedName);
  }

  onSave(): void {
    const educations = this.educationsInputComponent?.value || [];
    const trackRecords = this.trackRecordsInputComponent?.value || [];
    const hasIncompleteTrackRecord = trackRecords.some((record: TrackRecord) => {
      const startDate = record.workingUntilNow ? record.employmentDurationFromToNow : record.employmentDurationRange?.[0];
      const endDate = record.workingUntilNow ? true : record.employmentDurationRange?.[1];
      return !record.companyName?.trim() || !record.companyType?.name?.trim() || !record.sector?.name?.trim() ||
        !record.industry?.name?.trim() || !record.department?.name?.trim() || !record.jobTitle?.trim() ||
        !record.roleDetails?.trim() || !startDate || !endDate;
    });

    if (hasIncompleteTrackRecord) {
      this.trackRecordValidationError = this.translateService.instant("cv-review.validation.track-record");
      this.scrollToSection(this.trackRecordSection);
      return;
    }

    this.trackRecordValidationError = "";
    const hasIncompleteEducation = educations.some((education: Education) =>
      !education.universityName?.trim() ||
      !education.courseOfStudy?.trim() ||
      !education.graduationYear ||
      !education.degree?.name?.trim(),
    );

    if (hasIncompleteEducation) {
      this.educationValidationError = this.translateService.instant("cv-review.validation.education");
      this.scrollToSection(this.educationSection);
      return;
    }

    this.educationValidationError = "";
    this.loading = true;
    
    const mappedQualifications = this.certifications;

    this.store.dispatch(TalentProfileActions.updateTalentProfile({data:
        {
          ...this.talent,
          workExperience: {
             ...(this.talent.workExperience || {}),
             yearsOfExperience: this.yearsOfExperience
          },
          skills: Array.from(new Set([...(this.talent.skills || []), ...this.competences])),
          qualifications: Array.from(new Set([...(this.talent.qualifications || []), ...mappedQualifications])),
          education: [...this.talent.education, ...educations],
          trackRecord: [...this.talent.trackRecord, ...trackRecords]
        }
    }));
    this.store
      .select((state) => state[talentProfileFeatureName].entity)
      .pipe(
        filter((entity) => entity.status.loaded),
        map((entity) => entity.data),
        take(1),
      )
      .subscribe((candidate) => {
        this.loading = false;
        const notificationId = this.modalData["notification"]?.id;
        if (notificationId) {
          this.markNotificationAsRead(notificationId);
          this.notificationService.removeNotification(notificationId);
        }
        this.modal.close();
      });
  }

  private scrollToSection(section: ElementRef<HTMLElement>): void {
    setTimeout(() => section?.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  onCancel(): void {
    this.modal.close();
  }

  private markNotificationAsRead(notificationId?: string): void {
    if (notificationId) {
      this.notificationService.markReadNotifications([notificationId]).subscribe();
    }
  }
}
